import { useCallback } from 'react'
import useURLParams from 'hooks/useURLParams'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useUpdateRegistrationData } from 'hooks/queries/useUpdateRegistrationData'

type SelectionValue = string | number

interface UseSelectionHandlerOptions {
  paramName: string
  onNext?: (event?: any) => void
  dataKey: string
  generateEvent?: (value?: any) => any
}

/**
 * A hook for handling selection states based on device type (mobile/desktop)
 * with integrated data update functionality
 */
export const useSelectionHandler = <T extends SelectionValue>({
  paramName,
  onNext,
  dataKey,
  generateEvent,
}: UseSelectionHandlerOptions) => {
  const [searchParams, setSearchParams] = useURLParams({ pushNewState: false })
  const { isMobile } = useResponsiveDesign()
  const { mutateAsync: updateRegistrationData } = useUpdateRegistrationData()

  // Get the selected value from URL params
  const selectedValueStr = searchParams.get(paramName)
  const selectedValue = selectedValueStr as unknown as T

  const setValue = useCallback(
    (value: T) => {
      setSearchParams({ [paramName]: String(value) })
    },
    [paramName, setSearchParams],
  )

  const handleSelection = useCallback(
    (value: T) => {
      if (isMobile) {
        setValue(value)
        // Only for mobile: also submit immediately and move to next step
        updateRegistrationData({ [dataKey]: value }).then(() => {
          if (onNext) {
            const event = generateEvent ? generateEvent(value) : undefined
            onNext(event)
          }
        })
      } else {
        setValue(value)
      }
    },
    [isMobile, setValue, updateRegistrationData, dataKey, onNext, generateEvent],
  )

  const continueToNextStep = useCallback(async () => {
    if (!selectedValue) return

    await updateRegistrationData({ [dataKey]: selectedValue })
    if (onNext) {
      const event = generateEvent ? generateEvent(selectedValue) : undefined
      onNext(event)
    }
  }, [selectedValue, updateRegistrationData, dataKey, onNext, generateEvent])

  const handleSelectAndContinue = useCallback(
    async (value: T) => {
      setValue(value)
      await updateRegistrationData({ [dataKey]: value })
      if (onNext) {
        const event = generateEvent ? generateEvent(value) : undefined
        onNext(event)
      }
    },
    [setValue, updateRegistrationData, dataKey, onNext, generateEvent],
  )

  return {
    selectedValue,
    setValue,
    handleSelection,
    handleNext: continueToNextStep,
    handleSelectAndContinue,
    isSelected: (value: T) => selectedValue === value,
  }
}
